import { useEffect } from "react";

export type KeyboardHandlers = {
  onUndo?: () => void;
  onRedo?: () => void;
  onPrint?: () => void;
  onOpenCommandPalette?: () => void;
  onRotateSelected?: () => void;
  onCopy?: () => void;
  onPaste?: () => void;
  onDuplicate?: () => void;
  onSelectAll?: () => void;
  onNextSheet?: () => void;
  onPrevSheet?: () => void;
  onAlignLeft?: () => void;
  onAlignRight?: () => void;
  onAlignTop?: () => void;
  onAlignBottom?: () => void;
  onZoomIn?: () => void;
  onZoomOut?: () => void;
  onZoomFit?: () => void;
  onZoomToSelection?: () => void;
  onNudgeSelected?: (dx: number, dy: number) => void;
};

/**
 * Tangentbordsgenvägar:
 *   Ctrl/Cmd+Z              → undo
 *   Ctrl/Cmd+Y / Shift+Z    → redo
 *   Ctrl/Cmd+P              → exportera PDF (vi kallar fitView+window.print via handler)
 *   Ctrl/Cmd+K              → command palette
 *
 * Delete/Backspace hanteras av React Flow's deleteKeyCode-default.
 * Esc hanteras redan av React Flow för att avbryta drag/connection.
 * AutoCAD-genvägar (L=line, M=move, C=copy) ligger på command-palette-routen.
 */
export function useKeyboardShortcuts(handlers: KeyboardHandlers): void {
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      // Ignorera om fokus ligger i text/input — vanlig redigering av palette-sök
      // ska inte trigga ritningen:s genvägar.
      const target = e.target as HTMLElement | null;
      if (target) {
        const tag = target.tagName;
        if (tag === "INPUT" || tag === "TEXTAREA" || target.isContentEditable) {
          // Tillåt Ctrl+K / Ctrl+P även från inputs så palett/print kan öppnas
          // från sökrutan. Andra mod-genvägar hoppas över.
          const mod = e.ctrlKey || e.metaKey;
          if (!mod) return;
          const key = e.key.toLowerCase();
          if (key !== "k" && key !== "p") return;
        }
      }

      const mod = e.ctrlKey || e.metaKey;
      const key = e.key.toLowerCase();

      // Enstaka-bokstavsgenvägar utan modifier (AutoCAD-stil).
      if (!mod && !e.altKey) {
        if (key === "r" && !e.shiftKey) {
          e.preventDefault();
          handlers.onRotateSelected?.();
          return;
        }
        // Pilknappar nudgar markerade symboler — 1 rutsteg, eller 5 med Shift.
        // Utan markerade symboler gör React Flow sin vanliga pan istället.
        if (
          e.key === "ArrowUp" ||
          e.key === "ArrowDown" ||
          e.key === "ArrowLeft" ||
          e.key === "ArrowRight"
        ) {
          if (!handlers.onNudgeSelected) return;
          const step = e.shiftKey ? 5 : 1;
          let dx = 0;
          let dy = 0;
          if (e.key === "ArrowUp") dy = -step;
          else if (e.key === "ArrowDown") dy = step;
          else if (e.key === "ArrowLeft") dx = -step;
          else if (e.key === "ArrowRight") dx = step;
          e.preventDefault();
          handlers.onNudgeSelected(dx, dy);
          return;
        }
      }

      if (!mod) return;

      if (key === "z" && !e.shiftKey) {
        e.preventDefault();
        handlers.onUndo?.();
        return;
      }
      if ((key === "z" && e.shiftKey) || key === "y") {
        e.preventDefault();
        handlers.onRedo?.();
        return;
      }
      if (key === "p") {
        // Intercepta så vi hinner fitView innan print-dialogen öppnas.
        e.preventDefault();
        handlers.onPrint?.();
        return;
      }
      if (key === "k") {
        e.preventDefault();
        handlers.onOpenCommandPalette?.();
        return;
      }
      if (key === "c" && !e.shiftKey) {
        // Låt browsern kopiera text om användaren har textmarkering (palette-sökruta).
        const sel = window.getSelection();
        if (sel && !sel.isCollapsed && sel.toString().length > 0) return;
        e.preventDefault();
        handlers.onCopy?.();
        return;
      }
      if (key === "v" && !e.shiftKey) {
        e.preventDefault();
        handlers.onPaste?.();
        return;
      }
      if (key === "d" && !e.shiftKey) {
        e.preventDefault();
        handlers.onDuplicate?.();
        return;
      }
      if (key === "a" && !e.shiftKey) {
        e.preventDefault();
        handlers.onSelectAll?.();
        return;
      }
      // CAD-standard zoom-genvägar: Ctrl+0 passar in, Ctrl+plus/minus zoomar.
      // e.key är "+" / "-" oavsett shift eller keyboard layout; e.code kunde
      // också gå men är känsligare för sv-SE vs en-US layouts.
      if (key === "0" && !e.shiftKey) {
        e.preventDefault();
        handlers.onZoomFit?.();
        return;
      }
      if (e.key === "+" || e.key === "=") {
        e.preventDefault();
        handlers.onZoomIn?.();
        return;
      }
      if (e.key === "-" || e.key === "_") {
        e.preventDefault();
        handlers.onZoomOut?.();
        return;
      }
      // Sheet-byte. PageUp/PageDown rapporteras med e.key = "PageUp"/"PageDown",
      // och vi använder originalet (inte toLowerCase) för att särskilja dem tryggt.
      if (e.key === "PageDown") {
        e.preventDefault();
        handlers.onNextSheet?.();
        return;
      }
      if (e.key === "PageUp") {
        e.preventDefault();
        handlers.onPrevSheet?.();
        return;
      }

      // Alignment: Ctrl+Shift+[L/R/T/B]. Shift krävs för att inte krocka med
      // Ctrl+L (browser-fokus address bar) eller Ctrl+R (reload).
      if (e.shiftKey) {
        if (key === "l") {
          e.preventDefault();
          handlers.onAlignLeft?.();
          return;
        }
        if (key === "r") {
          e.preventDefault();
          handlers.onAlignRight?.();
          return;
        }
        if (key === "t") {
          e.preventDefault();
          handlers.onAlignTop?.();
          return;
        }
        if (key === "b") {
          e.preventDefault();
          handlers.onAlignBottom?.();
          return;
        }
        // Ctrl+Shift+F = zooma till markering (AutoCAD: Zoom Extents ~= "E").
        // Vi använder F eftersom Shift+E redan kan vara palette-inskrivning.
        if (key === "f") {
          e.preventDefault();
          handlers.onZoomToSelection?.();
          return;
        }
      }
    }

    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [
    handlers.onUndo,
    handlers.onRedo,
    handlers.onPrint,
    handlers.onOpenCommandPalette,
    handlers.onRotateSelected,
    handlers.onCopy,
    handlers.onPaste,
    handlers.onDuplicate,
    handlers.onSelectAll,
    handlers.onNextSheet,
    handlers.onPrevSheet,
    handlers.onAlignLeft,
    handlers.onAlignRight,
    handlers.onAlignTop,
    handlers.onAlignBottom,
    handlers.onZoomIn,
    handlers.onZoomOut,
    handlers.onZoomFit,
    handlers.onZoomToSelection,
    handlers.onNudgeSelected,
  ]);
}
